import { useMemo, useState, useEffect } from "react";
import { MAX_PAGE_SIZE } from "@/constants/pagination";
import type { ReactNode, CSSProperties } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import { Link, useLocation } from "react-router-dom";
import {
  FiSearch,
  FiCheck,
  FiX,
  FiEdit2,
  FiChevronLeft,
  FiChevronRight,
  FiXCircle,
} from "react-icons/fi";

import {
  useUsers,
  useUserModules,
} from "@/api/features/users/queries";
import {
  useGrantUserModulesMutation,
  useRevokeUserModulesMutation,
} from "@/api/features/users/mutations";
import { useModules } from "@/api/features/modules/queries";
import type { Module } from "@/api/features/modules/types";

/* ----------------------------- Data & Types ------------------------------ */

type UserRow = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
};

type ModulePickState = Record<number, boolean>; // moduleId → picked (for bulk / modal)

/* --------------------------------- UI Bits -------------------------------- */
function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");
    return { href, label };
  });
  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((c) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            <span className="text-gray-700">{c.label}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Modal({
  title,
  open,
  onClose,
  children,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-3xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
              onClick={onClose}
              aria-label="Close"
            >
              <FiX />
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-4 sm:p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({
  open,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
              onClick={onCancel}
            >
              <FiX />
            </button>
          </div>
          <div className="p-5 text-sm text-gray-700">{message}</div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              onClick={onCancel}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              <FiXCircle /> {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Page Component ----------------------------- */
export default function UserPermissions() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 8;

  // keep page in sync when search changes
  useEffect(() => {
    setPage(1);
  }, [q]);

  // 🔹 Real users from API
  const {
    data: userPage,
    isLoading: usersLoading,
    isError: usersError,
  } = useUsers({
    page,
    limit: pageSize,
    q: q.trim() || undefined,
  });

  // 🔹 Real modules from API
  const {
    data: modulesData,
    isLoading: modulesLoading,
    isError: modulesError,
  } = useModules({ page: 1, limit: MAX_PAGE_SIZE });

  const modules: Module[] = useMemo(() => {
    if (!modulesData) return [];
    // If your hook returns { items, total } adjust to modulesData.items
    return Array.isArray(modulesData)
      ? (modulesData as Module[])
      : // @ts-ignore – tweak according to your actual shape
        (modulesData.items as Module[]);
  }, [modulesData]);

  const total = userPage?.total ?? 0;

  // local "hidden" flag: this just removes rows from this table view (does NOT delete users)
  const [hidden, setHidden] = useState<Record<number, boolean>>({});

  // selection for bulk (keyed by userId)
  const [selected, setSelected] = useState<Record<number, boolean>>({});

  // per-user edit modal
  const [editUser, setEditUser] = useState<UserRow | null>(null);

  // bulk edit modal
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPick, setBulkPick] = useState<ModulePickState>({});

  // confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  // bulk grant / revoke mutations
  const grantMutation = useGrantUserModulesMutation();
  const revokeMutation = useRevokeUserModulesMutation();

  // 🔹 Map API users → rows
  const rows: UserRow[] = useMemo(() => {
    const items = userPage?.items ?? [];
    return items
      .filter((u) => !hidden[u.id])
      .map((u) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
      }));
  }, [userPage, hidden]);

  const allIds = rows.map((r) => r.id);
  const anySelected = allIds.some((id) => !!selected[id]);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => !!selected[id]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIndex = total === 0 ? 0 : (page - 1) * pageSize;
  const endIndex =
    total === 0 ? 0 : Math.min(startIndex + rows.length, total);

  /* ------------------------------ Handlers -------------------------------- */
  const toggleAll = (checked: boolean) => {
    const next: Record<number, boolean> = {};
    rows.forEach((r) => (next[r.id] = checked));
    setSelected(next);
  };

  const openBulk = () => {
    // initialize picks with all modules unchecked
    const init: ModulePickState = {};
    modules.forEach((m) => {
      init[m.id] = false;
    });
    setBulkPick(init);
    setBulkOpen(true);
  };

  const applyBulk = async (grant: boolean) => {
    const selectedIds = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([id]) => Number(id));

    const moduleIds = modules
      .filter((m) => bulkPick[m.id])
      .map((m) => m.id);

    if (!selectedIds.length || !moduleIds.length) {
      setBulkOpen(false);
      return;
    }

    const mutation = grant ? grantMutation : revokeMutation;

    try {
      await Promise.all(
        selectedIds.map((userId) =>
          mutation.mutateAsync({ userId, moduleIds })
        )
      );
    } finally {
      setBulkOpen(false);
    }
  };

  const askDelete = (user: UserRow) => {
    setConfirmMsg(
      `Do you want to remove "${user.firstName} ${user.lastName}" from the permissions table?`
    );
    setConfirmAction(() => () => {
      setHidden((prev) => ({ ...prev, [user.id]: true }));
      setSelected((prev) => {
        const copy = { ...prev };
        delete copy[user.id];
        return copy;
      });
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  /* --------------------------------- Render -------------------------------- */
  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            User Permissions
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search users…"
              className="w-64 rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </div>

          {/* Bulk edit */}
          <button
            onClick={openBulk}
            disabled={!anySelected || modules.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            title={
              modules.length === 0
                ? "Modules are still loading"
                : "Bulk edit permissions"
            }
          >
            Bulk Edit
          </button>
        </div>
      </div>

      {/* Table Card: sticky header + sticky first 3 columns + scroll */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {/* Important: scrolling container (header sticks inside this) */}
        <div className="relative overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full min-w-[1400px] text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="border-b border-gray-200">
                {/* Sticky header cells with fixed widths */}
                <Th
                  className="sticky top-0 left-0 z-40 bg-gray-50 w-12"
                  style={{ left: 0 }}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-sky-600"
                    checked={allSelected}
                    onChange={(e) => toggleAll(e.target.checked)}
                    aria-label="Select all"
                  />
                </Th>

                <Th
                  className="sticky top-0 z-30 bg-gray-50 w-16"
                  style={{ left: 48 }} // 12 * 4 = 48px
                >
                  S.No
                </Th>

                <Th
                  className="sticky top-0 z-30 bg-gray-50 w-[260px]"
                  style={{ left: 48 + 64 }} // 112px = 48 + 64
                >
                  User
                </Th>

                {modules.map((m) => (
                  <Th
                    key={m.id}
                    className="sticky top-0 z-20 bg-gray-50"
                  >
                    {m.name}
                  </Th>
                ))}

                <Th className="sticky top-0 z-20 bg-gray-50 text-right">
                  Actions
                </Th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {usersLoading || modulesLoading ? (
                <tr>
                  <td
                    colSpan={4 + modules.length}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Loading users and modules…
                  </td>
                </tr>
              ) : usersError || modulesError ? (
                <tr>
                  <td
                    colSpan={4 + modules.length}
                    className="px-5 py-10 text-center text-sm text-red-500"
                  >
                    Failed to load users or modules.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={4 + modules.length}
                    className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                rows.map((u, idx) => (
                  <UserPermissionsRow
                    key={u.id}
                    user={u}
                    index={idx}
                    pageStartIndex={startIndex}
                    modules={modules}
                    isSelected={!!selected[u.id]}
                    onToggleSelect={(checked) =>
                      setSelected((prev) => ({
                        ...prev,
                        [u.id]: checked,
                      }))
                    }
                    onOpenEdit={() => setEditUser(u)}
                    onAskDelete={askDelete}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
          <div className="text-xs text-gray-500">
            Showing{" "}
            <span className="font-medium text-gray-700">
              {total === 0 ? 0 : startIndex + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-700">
              {endIndex}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700">{total}</span>{" "}
            results
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <FiChevronLeft /> Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || total === 0}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Edit (per user) Modal */}
      <Modal
        title={
          editUser
            ? `Edit Permissions — ${editUser.firstName} ${editUser.lastName}`
            : "Edit Permissions"
        }
        open={!!editUser}
        onClose={() => setEditUser(null)}
      >
        {editUser && (
          <PerUserEditor
            user={editUser}
            modules={modules}
            onClose={() => setEditUser(null)}
          />
        )}
      </Modal>

      {/* Bulk Edit Modal */}
      <Modal
        title="Bulk Edit Permissions"
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
      >
        <BulkEditor
          modules={modules}
          picks={bulkPick}
          setPicks={setBulkPick}
          onGrant={() => applyBulk(true)}
          onRevoke={() => applyBulk(false)}
          selectedCount={Object.values(selected).filter(Boolean).length}
        />
      </Modal>

      {/* Confirm */}
      <ConfirmDialog
        open={confirmOpen}
        title="Remove user"
        message={confirmMsg}
        confirmText="Remove"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </AdminLayout>
  );
}

/* ------------------------- Row (per user, matrix) ------------------------ */

function UserPermissionsRow({
  user,
  index,
  pageStartIndex,
  modules,
  isSelected,
  onToggleSelect,
  onOpenEdit,
  onAskDelete,
}: {
  user: UserRow;
  index: number;
  pageStartIndex: number;
  modules: Module[];
  isSelected: boolean;
  onToggleSelect: (checked: boolean) => void;
  onOpenEdit: () => void;
  onAskDelete: (user: UserRow) => void;
}) {
  const { data: userModules, isLoading } = useUserModules(user.id);
  const grantMutation = useGrantUserModulesMutation();
  const revokeMutation = useRevokeUserModulesMutation();

  const allowedSet = useMemo(
    () =>
      new Set<number>(
        (userModules ?? []).map((m: Module) => m.id)
      ),
    [userModules]
  );

  const handleToggleCell = (moduleId: number, allowed: boolean) => {
    if (isLoading) return;

    if (allowed) {
      revokeMutation.mutate({
        userId: user.id,
        moduleIds: [moduleId],
      });
    } else {
      grantMutation.mutate({
        userId: user.id,
        moduleIds: [moduleId],
      });
    }
  };

  return (
    <tr className="hover:bg-gray-50/60">
      {/* Sticky body cells mirror header widths & offsets */}
      <Td
        className="sticky left-0 z-30 bg-white w-12"
        style={{ left: 0 }}
      >
        <input
          type="checkbox"
          className="h-4 w-4 accent-sky-600"
          checked={isSelected}
          onChange={(e) => onToggleSelect(e.target.checked)}
          aria-label={`Select ${user.firstName} ${user.lastName}`}
        />
      </Td>

      <Td
        className="sticky z-20 bg-white text-gray-700 w-16"
        style={{ left: 48 }}
      >
        {pageStartIndex + index + 1}
      </Td>

      <Td
        className="sticky z-20 bg-white w-[260px]"
        style={{ left: 48 + 64 }} // 112px
      >
        <div className="flex items-center gap-2.5">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
            {user.firstName[0]?.toUpperCase()}
            {user.lastName[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-gray-900">
              {user.firstName} {user.lastName}
            </div>
            <div className="truncate text-xs text-gray-500">
              {user.email}
            </div>
          </div>
        </div>
      </Td>

      {modules.map((m) => {
        const allowed = allowedSet.has(m.id);
        return (
          <Td key={m.id}>
            <button
              className={`grid h-8 w-8 place-items-center rounded-md ${
                allowed
                  ? "text-green-600 hover:bg-green-50"
                  : "text-gray-400 hover:bg-gray-100"
              }`}
              onClick={() => handleToggleCell(m.id, allowed)}
              title={
                allowed
                  ? "Allowed (click to revoke)"
                  : "Not allowed (click to grant)"
              }
              aria-label={`${m.name} permission`}
              disabled={isLoading}
            >
              {allowed ? <FiCheck /> : <FiX />}
            </button>
          </Td>
        );
      })}

      <Td>
        <div className="flex items-center justify-end gap-1.5">
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
            onClick={onOpenEdit}
            aria-label="Edit permissions"
            title="Edit permissions"
          >
            <FiEdit2 />
          </button>
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
            onClick={() => onAskDelete(user)}
            aria-label="Remove user from table"
            title="Remove row"
          >
            <FiX />
          </button>
        </div>
      </Td>
    </tr>
  );
}

/* --------------------------- Editors (Modals) ---------------------------- */

function PerUserEditor({
  user,
  modules,
  onClose,
}: {
  user: UserRow;
  modules: Module[];
  onClose: () => void;
}) {
  const { data: userModules, isLoading } = useUserModules(user.id);
  const grantMutation = useGrantUserModulesMutation();
  const revokeMutation = useRevokeUserModulesMutation();

  const [state, setState] = useState<ModulePickState>({});

  useEffect(() => {
    const allowedSet = new Set<number>(
      (userModules ?? []).map((m: Module) => m.id)
    );
    const initial: ModulePickState = {};
    modules.forEach((m) => {
      initial[m.id] = allowedSet.has(m.id);
    });
    setState(initial);
  }, [userModules, modules]);

  const handleToggleAll = (grant: boolean) => {
    const next: ModulePickState = {};
    modules.forEach((m) => {
      next[m.id] = grant;
    });
    setState(next);
  };

  const handleSave = async () => {
    const currentAllowed = new Set<number>(
      (userModules ?? []).map((m: Module) => m.id)
    );

    const toGrant: number[] = [];
    const toRevoke: number[] = [];

    modules.forEach((m) => {
      const initially = currentAllowed.has(m.id);
      const now = !!state[m.id];
      if (now && !initially) toGrant.push(m.id);
      if (!now && initially) toRevoke.push(m.id);
    });

    try {
      if (toGrant.length) {
        await grantMutation.mutateAsync({
          userId: user.id,
          moduleIds: toGrant,
        });
      }
      if (toRevoke.length) {
        await revokeMutation.mutateAsync({
          userId: user.id,
          moduleIds: toRevoke,
        });
      }
    } finally {
      onClose();
    }
  };

  const saving = grantMutation.isPending || revokeMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => handleToggleAll(true)}
          className="rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100"
          disabled={isLoading || saving}
        >
          Grant all
        </button>
        <button
          onClick={() => handleToggleAll(false)}
          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
          disabled={isLoading || saving}
        >
          Revoke all
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {modules.map((m) => (
          <label
            key={m.id}
            className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <span className="text-sm text-gray-800">{m.name}</span>
            <input
              type="checkbox"
              className="h-4 w-4 accent-sky-600"
              checked={!!state[m.id]}
              disabled={isLoading || saving}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  [m.id]: e.target.checked,
                }))
              }
            />
          </label>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={onClose}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
          disabled={isLoading || saving}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function BulkEditor({
  modules,
  picks,
  setPicks,
  onGrant,
  onRevoke,
  selectedCount,
}: {
  modules: Module[];
  picks: ModulePickState;
  setPicks: (v: ModulePickState) => void;
  onGrant: () => void;
  onRevoke: () => void;
  selectedCount: number;
}) {
  const togglePickAll = (checked: boolean) => {
    const next: ModulePickState = {};
    modules.forEach((m) => {
      next[m.id] = checked;
    });
    setPicks(next);
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-700">
        Selected users:{" "}
        <span className="font-medium text-gray-900">{selectedCount}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4 accent-sky-600"
            onChange={(e) => togglePickAll(e.target.checked)}
          />
          <span className="text-sm text-gray-700">Select all modules</span>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {modules.map((m) => (
          <label
            key={m.id}
            className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <span className="text-sm text-gray-800">{m.name}</span>
            <input
              type="checkbox"
              className="h-4 w-4 accent-sky-600"
              checked={!!picks[m.id]}
              onChange={(e) =>
                setPicks({
                  ...picks,
                  [m.id]: e.target.checked,
                })
              }
            />
          </label>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={onRevoke}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          Revoke for selected
        </button>
        <button
          onClick={onGrant}
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          Grant for selected
        </button>
      </div>
    </div>
  );
}

/* ------------------------------ Table helpers ----------------------------- */
function Th({
  children,
  className = "",
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <th
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
      style={style}
    >
      {children}
    </th>
  );
}
function Td({
  children,
  className = "",
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <td className={`px-5 py-3.5 ${className}`} style={style}>
      {children}
    </td>
  );
}
